import { create } from "zustand";

import { isDemoBoot } from "../lib/demo-mode.js";
import {
  DEFAULT_TODAY_LAYOUT,
  moveModule,
  normalizeLayout,
  TODAY_MAX_COLUMNS,
  type MoveAnchor,
  type TodayLayout,
  type TodayModuleId,
} from "../features/agenda/layout.js";

/** Today-screen module arrangement. DEVICE-LOCAL by design (like the
 *  Priorities view prefs): how someone lays out their columns depends on that
 *  device's window, so it's plain localStorage — never synced or backed up. */

const LS_KEY = "today_layout";

interface TodayLayoutState extends TodayLayout {
  /** Relocate a module to a column, next to an anchor (see `moveModule`). */
  move: (id: TodayModuleId, toCol: number, anchor: MoveAnchor, cols: number) => void;
  /** Set how many columns a module spans (1 = normal). */
  setSpan: (id: TodayModuleId, span: number) => void;
  reset: () => void;
}

function load(): TodayLayout {
  if (isDemoBoot()) return DEFAULT_TODAY_LAYOUT;
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? normalizeLayout(JSON.parse(raw)) : DEFAULT_TODAY_LAYOUT;
  } catch {
    return DEFAULT_TODAY_LAYOUT;
  }
}

function persist(layout: TodayLayout) {
  if (isDemoBoot()) return;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ order: layout.order, column: layout.column, span: layout.span }));
  } catch { /* storage full/blocked — the arrangement just doesn't stick */ }
}

export const useTodayLayoutStore = create<TodayLayoutState>((set, get) => ({
  ...load(),

  move(id, toCol, anchor, cols) {
    const { order, column, span } = get();
    const next = moveModule({ order, column, span }, id, toCol, anchor, cols);
    set(next);
    persist(next);
  },

  setSpan(id, span) {
    const clamped = Math.max(1, Math.min(TODAY_MAX_COLUMNS, Math.round(span)));
    const nextSpanMap = { ...get().span };
    if (clamped <= 1) delete nextSpanMap[id];
    else nextSpanMap[id] = clamped;
    const next = { order: get().order, column: get().column, span: nextSpanMap };
    set(next);
    persist(next);
  },

  reset() {
    set(DEFAULT_TODAY_LAYOUT);
    persist(DEFAULT_TODAY_LAYOUT);
  },
}));
